// ============================================================================
// EXPORT / SHARE UTILITIES
//
// Pure, side-effect-isolated helpers.  Nothing here touches React state.
// All browser-API calls (download anchor, canvas.toBlob, URL) are wrapped so
// callers can be tested by swapping out the helpers.
// ============================================================================

import type { ExecutionStep } from '../types/rust-engine-interfaces';

// ── JSON export ───────────────────────────────────────────────────────────────

export interface StepsExportEnvelope {
  /** Schema version — bump when the shape changes */
  version: 1;
  algorithmId: string;
  algorithmName: string;
  exportedAt: string;   // ISO-8601
  steps: ExecutionStep[];
}

/**
 * Serialises the current step array to a JSON blob and triggers a
 * browser download.  Returns the filename used.
 */
export function exportStepsAsJSON(
  steps: ExecutionStep[],
  algorithmId: string,
  algorithmName: string,
): string {
  const envelope: StepsExportEnvelope = {
    version: 1,
    algorithmId,
    algorithmName,
    exportedAt: new Date().toISOString(),
    steps,
  };

  const blob = new Blob([JSON.stringify(envelope, null, 2)], {
    type: 'application/json',
  });

  const filename = `${algorithmId}-steps-${Date.now()}.json`;
  triggerDownload(blob, filename);
  return filename;
}

/**
 * Validates and parses a raw JSON string produced by `exportStepsAsJSON`.
 * Throws a descriptive error if the envelope is malformed.
 */
export function parseStepsJSON(raw: string): StepsExportEnvelope {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    throw new Error('Invalid JSON — could not parse file.');
  }

  if (
    typeof parsed !== 'object' ||
    parsed === null ||
    (parsed as StepsExportEnvelope).version !== 1 ||
    !Array.isArray((parsed as StepsExportEnvelope).steps)
  ) {
    throw new Error('Unrecognised file format (expected version-1 steps envelope).');
  }

  return parsed as StepsExportEnvelope;
}

// ── PNG frame export ──────────────────────────────────────────────────────────

/**
 * Finds the first <canvas> element inside `container`, captures it as a
 * PNG blob, and triggers a browser download.
 *
 * Returns a Promise that resolves to the filename, or rejects with a
 * descriptive error if no canvas is found or the capture fails.
 */
export async function exportFrameAsPNG(
  container: HTMLElement,
  algorithmId: string,
): Promise<string> {
  const canvas = container.querySelector<HTMLCanvasElement>('canvas');
  if (!canvas) {
    throw new Error('No <canvas> element found in the visualizer container.');
  }

  return new Promise<string>((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('canvas.toBlob() returned null — canvas may be empty or tainted.'));
        return;
      }
      const filename = `${algorithmId}-frame-${Date.now()}.png`;
      triggerDownload(blob, filename);
      resolve(filename);
    }, 'image/png');
  });
}

// ── Share URL ─────────────────────────────────────────────────────────────────

export interface ShareParams {
  algorithmId: string;
  stepIndex: number;
}

const PARAM_ALGO  = 'algo';
const PARAM_STEP  = 'step';

/**
 * Builds a full URL (same origin) with `algo` and `step` query params.
 */
export function buildShareURL(params: ShareParams): string {
  const url = new URL(window.location.href);
  url.search = '';
  url.searchParams.set(PARAM_ALGO, params.algorithmId);
  url.searchParams.set(PARAM_STEP, String(params.stepIndex));
  return url.toString();
}

/**
 * Parses `algo` and `step` from the current URL's query string.
 * Returns null if neither param is present so callers can skip restoration.
 */
export function parseShareURL(search: string = window.location.search): ShareParams | null {
  const params = new URLSearchParams(search);
  const algorithmId = params.get(PARAM_ALGO);
  if (!algorithmId) return null;
  const stepRaw  = params.get(PARAM_STEP);
  const stepIndex = stepRaw !== null && /^\d+$/.test(stepRaw) ? parseInt(stepRaw, 10) : 0;
  return { algorithmId, stepIndex };
}

/**
 * Removes share params from the address bar without triggering a navigation
 * (uses history.replaceState).
 */
export function clearShareParams(): void {
  const url = new URL(window.location.href);
  url.searchParams.delete(PARAM_ALGO);
  url.searchParams.delete(PARAM_STEP);
  history.replaceState(null, '', url.toString());
}

// ── Clipboard helper ──────────────────────────────────────────────────────────

/**
 * Writes `text` to the system clipboard.
 * Returns true on success, false if the Clipboard API is unavailable.
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

// ── Internal helper ───────────────────────────────────────────────────────────

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a   = document.createElement('a');
  a.href     = url;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Revoke after a short delay so the browser has time to start the download
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
